import {useState} from "react";
import {useSelector} from "react-redux";
import moment from "moment";

import Button from "@mui/material/Button";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	IconButton,
	Checkbox,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import CloseIcon from "@mui/icons-material/Close";

import VLFDatePicker from "@/components/Input/DatePicker";
import VLFSelectNew from "@/components/Input/SelectNew";

import {AthleticTestAPI} from "@/api/athletic_test";
import {
	parseAthleticTestsWorkbook,
	matchPlayerRow,
	readWorkbookFromFile,
} from "@/utils/athleticTestImport";
import {SUCCESSFUL_OPERATION, ERROR_OPERATION} from "@/utils/constants";

// Import test atletici (portieri) da file OptoJump CSV/XLSX.
const ImportAthleticTestsModal = (props) => {
	const {open, onClose, players, handleNotification, onImported} = props;

	const clubId = useSelector((state) => state?.user?.clubId);
	const userId = useSelector((state) => state?.user?.userId);
	const userName = useSelector((state) => state?.user?.userName);

	const [step, setStep] = useState("upload"); // upload | review | result
	const [loading, setLoading] = useState(false);
	const [fileName, setFileName] = useState("");
	const [sessions, setSessions] = useState([]);
	const [warnings, setWarnings] = useState([]);
	// stato modificabile: date per sessione + match per riga
	const [sessionDates, setSessionDates] = useState({});
	const [matches, setMatches] = useState({}); // {sessionIdx: {rowIdx: playerId|null}}
	const [confidences, setConfidences] = useState({}); // {sessionIdx: {rowIdx: 'exact'|'probable'|'none'}}
	const [selected, setSelected] = useState({}); // {sessionIdx: {rowIdx: boolean}}
	const [result, setResult] = useState(null);

	const playerOptions = (players || []).map((p) => ({
		value: p.id,
		label: `${p.lastName} ${p.firstName || ""}`.trim(),
	}));

	const resetState = () => {
		setStep("upload");
		setLoading(false);
		setFileName("");
		setSessions([]);
		setWarnings([]);
		setSessionDates({});
		setMatches({});
		setConfidences({});
		setSelected({});
		setResult(null);
	};

	const handleClose = () => {
		resetState();
		onClose();
	};

	const handleFile = async (event) => {
		const file = event.target.files?.[0];
		event.target.value = null;
		if (!file) return;

		setLoading(true);
		setFileName(file.name);
		try {
			const workbook = await readWorkbookFromFile(file);
			const {sessions: parsed, warnings: parseWarnings} =
				parseAthleticTestsWorkbook(workbook);

			if (!parsed.length) {
				handleNotification(
					"Nessuna sessione con dati trovata nel file.",
					"error",
				);
				setLoading(false);
				return;
			}

			// pre-compila date e match automatici
			const nextDates = {};
			const nextMatches = {};
			const nextConfidences = {};
			const nextSelected = {};
			parsed.forEach((session, sIdx) => {
				nextDates[sIdx] = session.defaultDate || null;
				nextMatches[sIdx] = {};
				nextConfidences[sIdx] = {};
				nextSelected[sIdx] = {};
				session.rows.forEach((row, rIdx) => {
					const {player, confidence} = matchPlayerRow(row, players);
					nextMatches[sIdx][rIdx] = player ? player.id : null;
					nextConfidences[sIdx][rIdx] = confidence;
					nextSelected[sIdx][rIdx] = true; // di default tutte da importare
				});
			});

			setSessions(parsed);
			setWarnings(parseWarnings);
			setSessionDates(nextDates);
			setMatches(nextMatches);
			setConfidences(nextConfidences);
			setSelected(nextSelected);
			setStep("review");
		} catch (error) {
			handleNotification(
				"Impossibile leggere il file. Usa un CSV o XLSX valido.",
				"error",
			);
		} finally {
			setLoading(false);
		}
	};

	const toggleRow = (sIdx, rIdx, value) =>
		setSelected((prev) => ({
			...prev,
			[sIdx]: {...(prev[sIdx] || {}), [rIdx]: value},
		}));

	const toggleSession = (sIdx, session, value) =>
		setSelected((prev) => {
			const next = {...(prev[sIdx] || {})};
			session.rows.forEach((_, rIdx) => {
				next[rIdx] = value;
			});
			return {...prev, [sIdx]: next};
		});

	const sessionAllSelected = (sIdx, session) =>
		session.rows.every((_, rIdx) => selected[sIdx]?.[rIdx]);
	const sessionSomeSelected = (sIdx, session) =>
		session.rows.some((_, rIdx) => selected[sIdx]?.[rIdx]);

	// numero di righe selezionate CON un giocatore associato (davvero importabili)
	const importableCount = sessions.reduce(
		(acc, s, sIdx) =>
			acc +
			s.rows.filter(
				(r, rIdx) => selected[sIdx]?.[rIdx] && matches[sIdx]?.[rIdx],
			).length,
		0,
	);

	const canConfirm =
		sessions.length &&
		sessions.every((s, sIdx) => sessionDates[sIdx]) &&
		importableCount > 0;

	const runImport = async () => {
		setLoading(true);
		const created = [];
		const skipped = [];
		const failed = [];

		try {
			// carica i test esistenti per rilevare i duplicati (player + data + tipo)
			let existing = [];
			try {
				existing = await AthleticTestAPI.list({});
			} catch (e) {
				existing = [];
			}
			const existingKey = new Set(
				(existing || []).map((t) => {
					const pid = t.playerId || t.player?.id;
					return `${pid}|${moment(t.testDate).format("YYYY-MM-DD")}|${t.testType}`;
				}),
			);

			for (let sIdx = 0; sIdx < sessions.length; sIdx++) {
				const session = sessions[sIdx];
				const date = sessionDates[sIdx];
				const isoDay = moment(date).format("YYYY-MM-DD");

				for (let rIdx = 0; rIdx < session.rows.length; rIdx++) {
					const row = session.rows[rIdx];
					if (!selected[sIdx]?.[rIdx]) continue; // riga deselezionata: non importare
					const playerId = matches[sIdx]?.[rIdx];
					if (!playerId) {
						skipped.push({
							label: `${row.surname} (${session.title})`,
							reason: "nessun giocatore associato",
						});
						continue;
					}

					const key = `${playerId}|${isoDay}|${session.testType}`;
					if (existingKey.has(key)) {
						skipped.push({
							label: `${row.surname} (${session.title})`,
							reason: "test già presente in questa data",
						});
						continue;
					}

					const body = {
						playerId,
						testDate: moment(date).format("YYYY-MM-DDTHH:mm:ss"),
						testType: session.testType,
						details: Object.entries(row.values).map(([valueType, value]) => ({
							valueType,
							value,
						})),
						description: `Import OptoJump - ${session.title}`,
						clubId,
						userId,
						user: {userId, userName},
					};

					try {
						await AthleticTestAPI.create(body);
						existingKey.add(key); // evita doppioni nello stesso import
						created.push({label: `${row.surname} (${session.title})`});
					} catch (e) {
						failed.push({label: `${row.surname} (${session.title})`});
					}
				}
			}

			setResult({created, skipped, failed});
			setStep("result");
			if (created.length) {
				handleNotification(SUCCESSFUL_OPERATION, "success");
				onImported && onImported();
			}
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	const confidenceBadge = (confidence) => {
		const map = {
			exact: {label: "certo", color: "#2e7d32", bg: "rgba(46,125,50,0.12)"},
			probable: {label: "probabile", color: "#b26a00", bg: "rgba(178,106,0,0.12)"},
			none: {label: "da abbinare", color: "#c62828", bg: "rgba(198,40,40,0.12)"},
		};
		const c = map[confidence] || map.none;
		return (
			<span
				style={{
					color: c.color,
					backgroundColor: c.bg,
					borderRadius: "8px",
					padding: "2px 8px",
					fontSize: "0.75rem",
					fontWeight: 600,
					whiteSpace: "nowrap",
				}}
			>
				{c.label}
			</span>
		);
	};

	const renderUpload = () => (
		<div className="d-flex flex-column align-items-center gap-3 py-3">
			<div className="text-center" style={{maxWidth: 460}}>
				Carica il file dei test (esportato da Numbers in <b>CSV</b> o{" "}
				<b>XLSX</b>). Verranno rilevate le sessioni con dati e i relativi
				portieri.
			</div>
			<Button variant="contained" component="label" startIcon={<UploadFileIcon />}>
				Scegli file
				<input
					type="file"
					hidden
					accept=".csv,.xlsx,.xls"
					onChange={handleFile}
				/>
			</Button>
			{fileName ? <div className="text-muted">{fileName}</div> : null}
		</div>
	);

	const renderReview = () => (
		<div className="d-flex flex-column gap-3">
			{warnings.length ? (
				<div
					style={{
						backgroundColor: "rgba(178,106,0,0.10)",
						borderRadius: 10,
						padding: "8px 12px",
						fontSize: "0.85rem",
					}}
				>
					{warnings.map((w, i) => (
						<div key={i}>⚠️ {w}</div>
					))}
				</div>
			) : null}

			{sessions.map((session, sIdx) => (
				<div
					key={sIdx}
					style={{
						border: "1px solid #ebf1fd",
						borderRadius: 12,
						padding: 12,
					}}
				>
					<div className="d-flex align-items-center justify-content-between gap-2 flex-wrap">
						<div style={{fontWeight: 700}}>{session.title}</div>
						<div style={{minWidth: 200}}>
							<VLFDatePicker
								label="Data test"
								value={sessionDates[sIdx]}
								onChange={(value) =>
									setSessionDates((prev) => ({...prev, [sIdx]: value}))
								}
								format="DD-MM-YYYY"
								size="small"
								required
							/>
						</div>
					</div>

					<div className="mt-2">
						<table style={{width: "100%", borderCollapse: "collapse"}}>
							<thead>
								<tr style={{textAlign: "left", fontSize: "0.8rem", color: "#6b7a99"}}>
									<th style={{padding: "4px 6px", width: 40}}>
										<Checkbox
											size="small"
											checked={sessionAllSelected(sIdx, session)}
											indeterminate={
												sessionSomeSelected(sIdx, session) &&
												!sessionAllSelected(sIdx, session)
											}
											onChange={(e) =>
												toggleSession(sIdx, session, e.target.checked)
											}
										/>
									</th>
									<th style={{padding: "4px 6px"}}>File (cognome · anno)</th>
									<th style={{padding: "4px 6px"}}>Parametri</th>
									<th style={{padding: "4px 6px"}}>Match</th>
									<th style={{padding: "4px 6px", minWidth: 220}}>Giocatore</th>
								</tr>
							</thead>
							<tbody>
								{session.rows.map((row, rIdx) => {
									const isSelected = !!selected[sIdx]?.[rIdx];
									return (
										<tr
											key={rIdx}
											style={{
												borderTop: "1px solid #f2f5fb",
												opacity: isSelected ? 1 : 0.4,
											}}
										>
											<td style={{padding: "4px 6px"}}>
												<Checkbox
													size="small"
													checked={isSelected}
													onChange={(e) =>
														toggleRow(sIdx, rIdx, e.target.checked)
													}
												/>
											</td>
											<td style={{padding: "4px 6px"}}>
												{row.surname}
												{row.year ? ` · ${row.year}` : ""}
											</td>
											<td style={{padding: "4px 6px"}}>{row.valuesCount}</td>
											<td style={{padding: "4px 6px"}}>
												{confidenceBadge(confidences[sIdx]?.[rIdx])}
											</td>
											<td style={{padding: "4px 6px"}}>
												<VLFSelectNew
													options={playerOptions}
													value={
														playerOptions.find(
															(o) => o.value === matches[sIdx]?.[rIdx],
														) || null
													}
													onChange={(newValue) =>
														setMatches((prev) => ({
															...prev,
															[sIdx]: {
																...prev[sIdx],
																[rIdx]: newValue ? newValue.value : null,
															},
														}))
													}
													isClearable
													placeholder="Seleziona…"
												/>
											</td>
										</tr>
									);
								})}
							</tbody>
						</table>
					</div>
				</div>
			))}
		</div>
	);

	const renderResult = () => (
		<div className="d-flex flex-column gap-3">
			<div className="d-flex gap-3 flex-wrap">
				<ResultTile label="Creati" value={result?.created?.length || 0} color="#2e7d32" />
				<ResultTile label="Saltati" value={result?.skipped?.length || 0} color="#b26a00" />
				<ResultTile label="Falliti" value={result?.failed?.length || 0} color="#c62828" />
			</div>
			{result?.skipped?.length ? (
				<div>
					<div style={{fontWeight: 600, marginBottom: 4}}>Saltati</div>
					{result.skipped.map((s, i) => (
						<div key={i} style={{fontSize: "0.85rem"}}>
							• {s.label} — {s.reason}
						</div>
					))}
				</div>
			) : null}
			{result?.failed?.length ? (
				<div>
					<div style={{fontWeight: 600, marginBottom: 4, color: "#c62828"}}>
						Falliti
					</div>
					{result.failed.map((s, i) => (
						<div key={i} style={{fontSize: "0.85rem"}}>
							• {s.label}
						</div>
					))}
				</div>
			) : null}
		</div>
	);

	const content =
		step === "upload"
			? renderUpload()
			: step === "review"
				? renderReview()
				: renderResult();

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			maxWidth="md"
			fullWidth
			PaperProps={{className: "vlf-modal__paper"}}
		>
			<DialogTitle className="vlf-modal__title">
				<span className="vlf-modal__title-text">
					Importa test atletici (portieri)
				</span>
				<IconButton
					className="vlf-modal__close"
					size="small"
					onClick={handleClose}
					aria-label="chiudi"
				>
					<CloseIcon fontSize="small" />
				</IconButton>
			</DialogTitle>
			<DialogContent className="vlf-modal__content dialog-container">
				{content}
			</DialogContent>
			<DialogActions className="vlf-modal__actions">
				{step === "review" ? (
					<>
						<Button
							color="error"
							variant="outlined"
							onClick={() => setStep("upload")}
							disabled={loading}
						>
							Indietro
						</Button>
						<Button
							variant="contained"
							disabled={!canConfirm || loading}
							onClick={runImport}
						>
							{importableCount ? `Importa (${importableCount})` : "Importa"}
						</Button>
					</>
				) : step === "result" ? (
					<Button variant="contained" onClick={handleClose}>
						Chiudi
					</Button>
				) : (
					<Button color="error" variant="outlined" onClick={handleClose}>
						Annulla
					</Button>
				)}
			</DialogActions>
		</Dialog>
	);
};

const ResultTile = ({label, value, color}) => (
	<div
		style={{
			border: `1px solid ${color}33`,
			borderRadius: 12,
			padding: "10px 18px",
			textAlign: "center",
			minWidth: 90,
		}}
	>
		<div style={{fontSize: "1.4rem", fontWeight: 700, color}}>{value}</div>
		<div style={{fontSize: "0.8rem", color: "#6b7a99"}}>{label}</div>
	</div>
);

export default ImportAthleticTestsModal;
